import { useState, useEffect } from 'react';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5001';

export default function SettingsSection() {
  const [user, setUser] = useState<{ name?: string; email?: string } | null>(null);
  const [toasts, setToasts] = useState<any[]>([]);

  // Admin profile state
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');

  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  // Platform settings state
  const [platformName, setPlatformName] = useState('GCSE Computing');
  const [supportEmail, setSupportEmail] = useState('hi@gcsecomputing.co.uk');
  const [savingPlatform, setSavingPlatform] = useState(false);

  // Notification toggles
  const [notifyNewInquiry, setNotifyNewInquiry] = useState(true);
  const [notifyNewStudent, setNotifyNewStudent] = useState(true);
  const [notifyEventReminder, setNotifyEventReminder] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setUser(parsed);
        setAdminName(parsed.name || '');
        setAdminEmail(parsed.email || '');
      } catch { }
    }
  }, []);

  const addToast = (message: string, type = 'success') => {
    const id = Date.now();
    setToasts((t) => [...t, { id, message, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3000);
  };

  const handleSavePlatform = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingPlatform(true);
    // Simulate save (wire to backend when ready)
    await new Promise((r) => setTimeout(r, 1000));
    setSavingPlatform(false);
    addToast('Platform settings saved successfully!');
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      addToast('New passwords do not match', 'error');
      return;
    }
    if (newPassword.length < 8) {
      addToast('Password must be at least 8 characters', 'error');
      return;
    }
    setSavingPassword(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      if (res.ok) {
        addToast('Password changed successfully!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        const data = await res.json();
        addToast(data.error || 'Failed to change password', 'error');
      }
    } catch {
      addToast('Network error. Please try again.', 'error');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleSaveNotifications = () => {
    addToast('Notification preferences saved!');
  };

  const Toggle = ({ value, onChange }: { value: boolean; onChange: () => void }) => (
    <button
      onClick={onChange}
      className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${value ? 'bg-emerald-600' : 'bg-slate-200'}`}
    >
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${value ? 'translate-x-5' : 'translate-x-0'}`} />
    </button>
  );

  return (
    <div className="flex flex-col gap-6">
      {/* Toast container */}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-2">
        {toasts.map((t) => (
          <div key={t.id} className={`min-w-[220px] px-4 py-2 rounded-lg text-sm flex items-center gap-2 shadow-lg animate-in slide-in-from-right-full duration-300 ${t.type === 'error' ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-emerald-50 border border-emerald-200 text-emerald-700'}`}>
            <i className={t.type === 'error' ? 'ri-error-warning-line' : 'ri-check-line'} /> {t.message}
          </div>
        ))}
      </div>

      <div>
        <h2 className="text-xl font-bold text-slate-800" style={{ fontFamily: "'Inter', sans-serif" }}>Settings</h2>
        <p className="text-slate-500 text-sm">Manage platform configuration, account and notifications</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column */}
        <div className="lg:col-span-2 flex flex-col gap-6">

          {/* Platform Settings */}
          <section className="bg-white rounded-xl border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
              <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center">
                <i className="ri-settings-3-line text-emerald-700"></i>
              </div>
              <div>
                <h3 className="font-bold text-slate-800 text-sm">Platform Settings</h3>
                <p className="text-xs text-slate-400">General site configuration</p>
              </div>
            </div>
            <form onSubmit={handleSavePlatform} className="p-6 flex flex-col gap-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Platform Name</label>
                  <input value={platformName} onChange={(e) => setPlatformName(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Support Email</label>
                  <input type="email" value={supportEmail} onChange={(e) => setSupportEmail(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500" />
                </div>
                {/* Timezone label removed */}
                {/* API Base URL removed from settings UI */}
              </div>
              <div className="flex justify-end pt-1">
                <button type="submit" disabled={savingPlatform} className="bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold px-5 py-2 rounded-lg transition-colors cursor-pointer disabled:opacity-75 flex items-center gap-2">
                  {savingPlatform ? <><i className="ri-loader-4-line animate-spin"></i> Saving...</> : <><i className="ri-save-line"></i> Save Settings</>}
                </button>
              </div>
            </form>
          </section>

          {/* Change Password */}
          <section className="bg-white rounded-xl border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
              <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center">
                <i className="ri-lock-password-line text-amber-600"></i>
              </div>
              <div>
                <h3 className="font-bold text-slate-800 text-sm">Change Password</h3>
                <p className="text-xs text-slate-400">Update your admin account password</p>
              </div>
            </div>
            <form onSubmit={handleChangePassword} className="p-6 flex flex-col gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Current Password</label>
                <div className="relative">
                  <input
                    type={showPasswords ? 'text' : 'password'}
                    required
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500 pr-10"
                    placeholder="Enter current password"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">New Password</label>
                  <input type={showPasswords ? 'text' : 'password'} required value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500" placeholder="Min. 8 characters" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Confirm New Password</label>
                  <input type={showPasswords ? 'text' : 'password'} required value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500" placeholder="Repeat new password" />
                </div>
              </div>
              <div className="flex items-center justify-between pt-1">
                <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-500">
                  <input type="checkbox" checked={showPasswords} onChange={() => setShowPasswords(!showPasswords)} className="rounded" />
                  Show passwords
                </label>
                <button type="submit" disabled={savingPassword} className="bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold px-5 py-2 rounded-lg transition-colors cursor-pointer disabled:opacity-75 flex items-center gap-2">
                  {savingPassword ? <><i className="ri-loader-4-line animate-spin"></i> Updating...</> : <><i className="ri-lock-line"></i> Update Password</>}
                </button>
              </div>
            </form>
          </section>

          {/* Notifications */}
          <section className="bg-white rounded-xl border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                <i className="ri-notification-3-line text-blue-600"></i>
              </div>
              <div>
                <h3 className="font-bold text-slate-800 text-sm">Notification Preferences</h3>
                <p className="text-xs text-slate-400">Choose what alerts you receive</p>
              </div>
            </div>
            <div className="p-6 flex flex-col gap-4">
              {[
                { label: 'New Inquiry Received', desc: 'Get notified when a user submits a contact inquiry', value: notifyNewInquiry, onChange: () => setNotifyNewInquiry(!notifyNewInquiry) },
                { label: 'New Student Registered', desc: 'Alert when a student creates a new account', value: notifyNewStudent, onChange: () => setNotifyNewStudent(!notifyNewStudent) },
                { label: 'Upcoming Event Reminder', desc: 'Reminder 24 hours before a scheduled event', value: notifyEventReminder, onChange: () => setNotifyEventReminder(!notifyEventReminder) },
              ].map((n) => (
                <div key={n.label} className="flex items-center justify-between gap-4 py-1">
                  <div>
                    <p className="text-sm font-medium text-slate-700">{n.label}</p>
                    <p className="text-xs text-slate-400">{n.desc}</p>
                  </div>
                  <Toggle value={n.value} onChange={n.onChange} />
                </div>
              ))}
              <div className="flex justify-end pt-2 border-t border-slate-100">
                <button onClick={handleSaveNotifications} className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-5 py-2 rounded-lg transition-colors cursor-pointer flex items-center gap-2">
                  <i className="ri-save-line"></i> Save Preferences
                </button>
              </div>
            </div>
          </section>

        </div>

        {/* Right column */}
        <div className="flex flex-col gap-6">

          {/* Admin Profile Card */}
          <section className="bg-white rounded-xl border border-slate-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100">
              <h3 className="font-bold text-slate-800 text-sm">Admin Profile</h3>
            </div>
            <div className="p-5 flex flex-col items-center text-center gap-3">
              <div className="w-16 h-16 bg-emerald-700 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                {(user?.name || 'A')[0].toUpperCase()}
              </div>
              <div>
                <p className="font-bold text-slate-800">{user?.name || 'Super Admin'}</p>
                <p className="text-xs text-slate-400">{user?.email || 'hi@gcsecomputing.co.uk'}</p>
              </div>
              <span className="bg-emerald-50 text-emerald-700 text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1">
                <i className="ri-shield-check-line"></i> Admin
              </span>
            </div>
          </section>

          {/* Danger Zone removed from settings UI */}

        </div>
      </div>
    </div>
  );
}
